import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { billingAPI } from '../../services/api'
import { t, locale } from '../../i18n'

const INCLUDED = [
  'Your own website and subdomain',
  'Arabic and English',
  'Orders, bookings and customers',
  'Cash, card and bank transfer',
  'Delivery areas with your own prices',
  'No commission on your sales',
]

const price = plan => new Intl.NumberFormat(locale(), {
  style: 'currency', currency: (plan.currency || 'OMR').toUpperCase(), maximumFractionDigits: 2,
}).format(plan.base_price_cents / 100)

// The platform's live plans (Platform > Plans). Before any plan exists it
// shows what every account includes.
export default function PricingSection({ heading = true }) {
  const [plans, setPlans] = useState(null)
  useEffect(() => { billingAPI.getPublicPlans().then(r => setPlans(r.data.plans || [])).catch(() => setPlans([])) }, [])

  return (
    <section id="pricing" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      {heading && (
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold text-blue-700">{t('Pricing')}</p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Simple monthly plans. No commission.')}</h2>
          <p className="mt-3 text-lg text-slate-600">{t('Start with a free trial, then pick the plan that fits. Change or cancel any time.')}</p>
        </div>
      )}

      {plans && plans.length > 0 ? (
        <div className={`mt-12 grid gap-6 ${plans.length >= 3 ? 'lg:grid-cols-3' : plans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'max-w-md mx-auto'}`}>
          {plans.map((plan, i) => {
            const featured = plans.length >= 3 ? i === 1 : i === plans.length - 1
            const features = plan.features.length ? plan.features : INCLUDED.slice(0, 4)
            return (
              <div key={plan.id} className={`relative flex flex-col rounded-2xl border p-7 ${featured ? 'border-blue-700 shadow-xl shadow-blue-900/10' : 'border-slate-200'}`}>
                {featured && <span className="absolute -top-3 start-7 rounded-full bg-blue-700 px-3 py-1 text-xs font-semibold text-white">{t('Most popular')}</span>}
                <h3 className="text-lg font-bold">{plan.name}</h3>
                <p className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold tracking-tight">{price(plan)}</span>
                  <span className="text-slate-500">/ {t(plan.interval === 'year' ? 'year' : 'month')}</span>
                </p>
                <ul className="mt-6 flex-1 space-y-3 text-sm text-slate-700">
                  {features.map(f => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{t(f)}</li>)}
                </ul>
                <Link to="/get-started" className={`mt-8 rounded-xl py-3 text-center font-semibold ${featured ? 'bg-blue-700 text-white hover:bg-blue-800' : 'border border-slate-300 text-slate-900 hover:bg-slate-50'}`}>{t('Start free trial')}</Link>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-slate-200 p-8 sm:p-10">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h3 className="text-xl font-bold">{t('Every account includes')}</h3>
              <ul className="mt-5 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                {INCLUDED.map(f => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{t(f)}</li>)}
              </ul>
            </div>
            <Link to="/get-started" className="rounded-xl bg-blue-700 px-6 py-3 text-center font-semibold text-white hover:bg-blue-800">{t('Start free trial')}</Link>
          </div>
        </div>
      )}
    </section>
  )
}
